<template>
    <div>
        <page-header :title="title" :items="items" />

        <div v-if="!editMode || (editMode && user.id)" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <add-edit-form
                            v-model="user"
                            :edit-mode="editMode"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                    </div>
                </div>

                <div v-if="editMode && user.id && !isAdmin" class="card mt-3">
                    <div class="card-body">
                        <h5 class="card-title mb-3">
                            This user has {{ user.stars }} star{{
                                user.stars !== 1 ? 's' : ''
                            }}
                        </h5>
                        <stars-management
                            :user-id="user.id"
                            @change-stars="user.stars += $event"
                        />
                    </div>
                </div>
                <div v-if="editMode && user.id && !isAdmin" class="card mt-3">
                    <div class="card-body">
                        <h5 class="card-title mb-3">
                            <span> User orders </span>
                        </h5>
                        <user-orders :user-id="user.id" />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div :class="{ 'mb-3': editMode }">
                            <div v-if="isLoggedUser">
                                <div class="text-center p-1">
                                    You can only edit your account through 'My
                                    account'
                                </div>

                                <button
                                    class="btn btn-success btn-static waves-effect waves-light m-1"
                                    @click="$router.push('/account')"
                                >
                                    My account
                                </button>
                            </div>

                            <button
                                v-else
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                            <button
                                v-if="editMode"
                                v-b-modal.send-email-to-user-modal
                                class="btn btn-success waves-effect waves-light m-1"
                            >
                                <i class="fas fa-envelope" />
                                Send E-mail
                            </button>
                        </div>

                        <item-information
                            :edit-mode="editMode"
                            :sections="sections"
                            :item="user"
                            :campuses="user.campuses"
                            @on-delete-confirm="onDeleteConfirm"
                            @on-campuses-change="onCampusesChange"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />

        <send-email-modal :user="user" />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { StatusCodes as HTTP } from 'http-status-codes';
import { required, email, minLength } from 'vuelidate/lib/validators';
import UserOrders from '@/components/users/UserOrders';
import AddEditForm from '@/components/users/AddEditForm';
import SendEmailModal from '@/components/users/SendEmailModal';
import validatePhoneFormat from '@/helpers/validatePhoneFormat';
import StarsManagement from '@/components/users/StarsManagement';
import ItemInformation from '@/components/elements/ItemInformation';

export default {
    page: {
        title: 'Manage User'
    },

    validations: {
        user: {
            email: {
                required,
                email
            },
            roles: {
                required
            },
            tz: {
                required
            },
            firstName: {
                required,
                minLength: minLength(2)
            },
            lastName: {
                required,
                minLength: minLength(2)
            },
            phone: {
                required,
                isFormatValid: v => validatePhoneFormat(v)
            },
            hugs: {
                maxElements: value => value.length <= 2
            }
        }
    },

    components: {
        AddEditForm,
        SendEmailModal,
        StarsManagement,
        ItemInformation,
        UserOrders
    },

    data() {
        const {
            params: { id = null },
            query: { selectedRole }
        } = this.$route;

        return {
            title: id ? 'Edit user' : 'Add user',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Users',
                    to: '/users'
                },
                {
                    text: id ? 'Edit' : 'Add',
                    active: true
                }
            ],
            id,
            editMode: !!id,
            user: {
                campuses: [],
                hugs: [],
                permissions: {},
                roles: [id] || !selectedRole ? [] : [selectedRole]
            },
            serverErrors: []
        };
    },

    computed: {
        ...mapGetters({
            ADMIN_ROLE: 'roles/ADMIN',
            ALL_ROLES: 'roles/ALL_ROLES',
            loggedUser: 'auth/loggedUser'
        }),

        sections() {
            const sections = {
                campuses: !this.isLoggedUser
            };

            if (this.editMode) {
                sections.status = true;
            }

            return sections;
        },

        isLoggedUser() {
            return this.loggedUser.id === this.id;
        },

        isAdmin() {
            return this.user.roles.includes(this.ADMIN_ROLE);
        }
    },

    async created() {
        if (this.editMode) {
            try {
                this.user = await this.getUser(this.id);

                this.user.roles = this.user.roles.map(role => role.name);
                this.user.permissions = this.user.acl?.permissions || {};
            } catch (error) {
                this.$toasterError();

                console.error(error);
            }

            return;
        }
    },

    methods: {
        ...mapActions({
            addUser: 'users/store',
            deleteUser: 'users/destroy',
            getUser: 'users/show',
            updateUser: 'users/update'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            const campuses = this.user.campuses.map(campus => campus.id);
            const hugs = this.user.hugs.map(hug => hug.id);

            const data = {
                ...this.user,
                campuses,
                hugs
            };

            try {
                if (!this.editMode) {
                    const added = await this.addUser(data);

                    this.$router.push(`/users/${added.id}/edit`);
                } else {
                    const user = await this.updateUser({
                        id: this.id,
                        data
                    });

                    this.user.updatedAt = user.updatedAt;

                    this.$toaster('User updated!');
                }
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteUser(this.id);

                this.$router.push('/users');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.UNPROCESSABLE_ENTITY && data) {
                        this.$toasterError(data);

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        onCampusesChange(campuses) {
            this.user.campuses = campuses;
            this.informationKey++;
        }
    }
};
</script>
